import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/site";
import { setToken, formatApiError } from "@/lib/admin";

const inputCls = "w-full border border-line bg-white px-4 py-3.5 text-sm text-ink outline-none placeholder:text-neutral-400 focus:border-regal-amber transition-colors";

export default function SignIn() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    document.title = "Sign In | Regal Tuning";
    // already signed in? go straight to the account area
    axios.get(`${API}/account/me`, { withCredentials: true })
      .then(() => nav("/account", { replace: true }))
      .catch(() => setChecking(false));
  }, [nav]);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      setToken(data.token);
      nav("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (checking) return null;

  return (
    <main className="bg-surface" data-testid="sign-in-page">
      <div className="mx-auto max-w-md px-5 py-16 sm:py-24">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber">Account</p>
        <h1 className="mt-2 font-display text-3xl font-bold uppercase tracking-[0.04em] text-ink">Sign in</h1>
        <p className="mt-2 text-sm text-neutral-500">
          Track your orders and check out faster. Customers sign in with Google — no password needed.
        </p>

        <button
          onClick={googleSignIn}
          data-testid="google-sign-in-button"
          className="mt-8 flex w-full items-center justify-center gap-3 border border-line bg-white px-6 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-ink transition-colors hover:border-regal-amber hover:text-regal-amber"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45c-.28 1.48-1.12 2.73-2.38 3.57v2.97h3.85c2.26-2.08 3.58-5.15 3.58-8.73z"/>
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.85-2.97c-1.07.72-2.44 1.14-4.08 1.14-3.14 0-5.8-2.12-6.75-4.97H1.29v3.06C3.26 21.3 7.31 24 12 24z"/>
            <path fill="#FBBC05" d="M5.25 14.29c-.24-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.65H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.35l3.96-3.06z"/>
            <path fill="#EA4335" d="M12 4.74c1.76 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.65l3.96 3.06C6.2 6.86 8.86 4.74 12 4.74z"/>
          </svg>
          Continue with Google
        </button>

        <div className="my-8 flex items-center gap-4">
          <span className="h-px flex-1 bg-line" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Staff &amp; trade</span>
          <span className="h-px flex-1 bg-line" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="staff-sign-in-form">
          <input type="email" required placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls} data-testid="sign-in-email-input" />
          <input type="password" required placeholder="Password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputCls} data-testid="sign-in-password-input" />
          {error && <p className="text-sm text-red-600" data-testid="sign-in-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="sign-in-submit-button"
            className="w-full bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-void transition-colors hover:bg-amber-400 disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-neutral-500">
          New customer? Just use Google above — your account is created automatically.
        </p>
        <p className="mt-2 text-center text-xs">
          <Link to="/" className="text-regal-amber hover:underline" data-testid="sign-in-home-link">Back to homepage</Link>
        </p>
      </div>
    </main>
  );
}
